import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Alert, AlertDescription } from "../components/ui/alert";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import { 
  DollarSign,
  Plus,
  ArrowLeft,
  Calendar,
  CreditCard,
  CheckCircle,
  Clock,
  AlertTriangle,
  Download,
  RefreshCw,
  TrendingUp,
  Users,
  Receipt
} from "lucide-react";

const PAYMENT_TYPES = [
  { value: "annual_dues", label: "Annual Dues" },
  { value: "special_assessment", label: "Special Assessment" },
  { value: "fine", label: "Fine" },
  { value: "drc_deposit", label: "DRC Deposit" }
];

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending", color: "bg-yellow-500/10 text-yellow-600" },
  { value: "completed", label: "Completed", color: "bg-green-500/10 text-green-600" },
  { value: "failed", label: "Failed", color: "bg-red-500/10 text-red-600" },
  { value: "refunded", label: "Refunded", color: "bg-purple-500/10 text-purple-600" }
];

const PaymentsPage = ({ isBoard = false }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  
  // Create payment state
  const [createOpen, setCreateOpen] = useState(false);
  const [newPayment, setNewPayment] = useState({
    owner_id: "",
    property_id: "",
    amount: "",
    payment_type: "",
    description: "",
    due_date: ""
  });
  
  // Pay/checkout state
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [autoPayOpen, setAutoPayOpen] = useState(false);
  const [autoPayAmount, setAutoPayAmount] = useState("250");
  const [autoPayInterval, setAutoPayInterval] = useState("monthly");
  const [subscriptions, setSubscriptions] = useState([]);
  const [autoPayLoading, setAutoPayLoading] = useState(false);

  const fetchSubscriptions = async () => {
    try {
      const endpoint = isBoard ? `${API}/recurring-payments/admin/all` : `${API}/recurring-payments/my-subscriptions`;
      const res = await axios.get(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      setSubscriptions(res.data.subscriptions || []);
    } catch (error) {
      console.error("Failed to fetch subscriptions");
    }
  };

  const handleSetupAutoPay = async () => {
    try {
      setAutoPayLoading(true);
      const res = await axios.post(`${API}/recurring-payments/setup`, {
        amount: parseFloat(autoPayAmount),
        interval: autoPayInterval,
        description: "HOA Dues"
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.success(res.data.message || "Auto-pay set up successfully");
        setAutoPayOpen(false);
        fetchSubscriptions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set up auto-pay");
    } finally {
      setAutoPayLoading(false);
    }
  };

  const handleCancelAutoPay = async (subscriptionId) => {
    try {
      await axios.post(`${API}/recurring-payments/cancel/${subscriptionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Auto-pay cancelled");
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel");
    }
  };

  const fetchPayments = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (typeFilter && typeFilter !== "all") params.append("payment_type", typeFilter);
      
      const response = await axios.get(`${API}/payments?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data);
    } catch (error) {
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    if (!isBoard) return;
    try {
      const response = await axios.get(`${API}/payments/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to load dashboard");
    }
  };

  const fetchOwnersAndProperties = async () => {
    if (!isBoard) return;
    try {
      const [ownersRes, propsRes] = await Promise.all([
        axios.get(`${API}/owners`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setOwners(ownersRes.data);
      setProperties(propsRes.data);
    } catch (error) {
      console.error("Failed to load data");
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchDashboard();
    fetchOwnersAndProperties();
    fetchSubscriptions();
    
    // Check for successful payment return
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      handlePaymentReturn(sessionId);
    }
  }, [token, statusFilter, typeFilter]);

  const handlePaymentReturn = async (sessionId) => {
    // Find the payment with this session
    try {
      const paymentWithSession = payments.find(p => p.stripe_session_id === sessionId);
      if (paymentWithSession) {
        await axios.post(
          `${API}/payments/${paymentWithSession.payment_id}/confirm?session_id=${sessionId}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Payment confirmed!");
        fetchPayments();
      }
    } catch (error) {
      console.error("Error confirming payment:", error);
    }
  };

  const handleCreatePayment = async () => {
    if (!newPayment.owner_id || !newPayment.amount || !newPayment.payment_type) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.post(`${API}/payments`, {
        ...newPayment,
        amount: parseFloat(newPayment.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payment created");
      setCreateOpen(false);
      setNewPayment({ owner_id: "", property_id: "", amount: "", payment_type: "", description: "", due_date: "" });
      fetchPayments();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment");
    }
  };

  const handlePayNow = async () => {
    if (!selectedPayment) return;
    
    setProcessing(true);
    try {
      const currentUrl = window.location.origin + window.location.pathname;
      const response = await axios.post(
        `${API}/payments/${selectedPayment.payment_id}/create-checkout?success_url=${encodeURIComponent(currentUrl)}&cancel_url=${encodeURIComponent(currentUrl)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.mock_mode) {
        // Mock mode - simulate successful payment
        await axios.post(
          `${API}/payments/${selectedPayment.payment_id}/confirm?session_id=${response.data.session_id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Payment completed (mock mode)");
        setPayOpen(false);
        fetchPayments();
        fetchDashboard();
      } else {
        // Real Stripe - redirect to checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process payment");
    } finally {
      setProcessing(false);
    }
  };

  const handleRefund = async (paymentId) => {
    const reason = window.prompt("Enter reason for refund:");
    if (!reason) return;
    
    try {
      await axios.post(
        `${API}/payments/${paymentId}/refund?reason=${encodeURIComponent(reason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Payment refunded");
      fetchPayments();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process refund");
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_OPTIONS.find(s => s.value === status);
    return config ? (
      <Badge className={config.color}>{config.label}</Badge>
    ) : (
      <Badge variant="outline">{status}</Badge>
    );
  };

  const getTypeLabel = (type) => {
    return PAYMENT_TYPES.find(t => t.value === type)?.label || type;
  };

  const content = (
    <div className={isBoard ? "p-6 lg:p-8 max-w-7xl mx-auto" : "min-h-screen bg-background"}>
      {/* Header - only for non-board */}
      {!isBoard && (
        <header className="border-b bg-card/95 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/owner")} className="text-muted-foreground hover:text-foreground transition-colors duration-200">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-success" />
                </div>
                <div>
                  <span className="font-serif text-lg font-semibold block">My Payments</span>
                  <span className="text-xs text-muted-foreground">View and pay your dues</span>
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Page Title for Board */}
      {isBoard && (
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-success" />
          </div>
          <div>
            <span className="font-serif text-lg font-semibold block">Payments & Billing</span>
            <span className="text-xs text-muted-foreground">Manage dues and transactions</span>
          </div>
        </div>
      )}

      <div className={isBoard ? "" : "max-w-7xl mx-auto px-4 md:px-8 py-8"}>
        {/* Dashboard for Board */}
        {isBoard && dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
            <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.1s' }}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Collected</p>
                    <p className="text-2xl font-bold text-success mt-1">${(dashboard.total_collected ?? dashboard.total_received ?? 0).toLocaleString()}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <TrendingUp className="w-5 h-5 text-success" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.2s' }}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pending</p>
                    <p className="text-2xl font-bold text-warning mt-1">${(dashboard.total_pending ?? 0).toLocaleString()}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Clock className="w-5 h-5 text-warning" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.3s' }}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Overdue</p>
                    <p className="text-2xl font-bold text-destructive mt-1">${(dashboard.total_overdue ?? dashboard.total_outstanding ?? 0).toLocaleString()}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <AlertTriangle className="w-5 h-5 text-destructive" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.4s' }}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Transactions</p>
                    <p className="text-2xl font-bold mt-1">{dashboard.total_transactions ?? dashboard.payment_count ?? 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Receipt className="w-5 h-5 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Mock Mode Alert */}
        <Alert className="mb-8 border-warning/20 bg-warning/5">
          <CreditCard className="h-4 w-4 text-warning" />
          <AlertDescription>
            <strong>Demo Mode:</strong> Stripe is not configured. Payments are simulated for testing.
            In production, owners will be redirected to Stripe Checkout for secure payment processing.
          </AlertDescription>
        </Alert>

        {/* Auto-Pay Section */}
        <Card className="mb-8 border-primary/20" data-testid="auto-pay-section">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-primary" />
                  Auto-Pay / Recurring Payments
                </CardTitle>
                <CardDescription className="mt-1">Set up automatic payments for HOA dues</CardDescription>
              </div>
              {!isBoard && (
                <Button size="sm" onClick={() => setAutoPayOpen(true)} data-testid="setup-autopay-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Set Up Auto-Pay
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {subscriptions.length > 0 ? (
              <div className="space-y-3">
                {subscriptions.map((sub) => (
                  <div key={sub.subscription_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border" data-testid={`subscription-${sub.subscription_id}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">${sub.amount?.toFixed(2)}</span>
                        <Badge variant={sub.active ? "default" : "secondary"}>
                          {sub.active ? "Active" : "Cancelled"}
                        </Badge>
                        <Badge variant="outline" className="capitalize">{sub.interval}</Badge>
                        {sub.mock && <Badge variant="outline" className="text-amber-600">Mock</Badge>}
                      </div>
                      {isBoard && sub.owner_info && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {sub.owner_info.first_name} {sub.owner_info.last_name}
                        </p>
                      )}
                    </div>
                    {sub.active && (
                      <Button variant="outline" size="sm" onClick={() => handleCancelAutoPay(sub.subscription_id)} data-testid={`cancel-autopay-${sub.subscription_id}`}>
                        Cancel
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-2">
                {isBoard ? "No active auto-pay subscriptions" : "Set up auto-pay to never miss a payment"}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Auto-Pay Setup Dialog */}
        <Dialog open={autoPayOpen} onOpenChange={setAutoPayOpen}>
          <DialogContent data-testid="autopay-setup-dialog">
            <DialogHeader>
              <DialogTitle>Set Up Auto-Pay</DialogTitle>
              <DialogDescription>Configure recurring payments for your HOA dues</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Amount per Payment</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    className="pl-7"
                    value={autoPayAmount}
                    onChange={(e) => setAutoPayAmount(e.target.value)}
                    data-testid="autopay-amount-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Payment Frequency</Label>
                <Select value={autoPayInterval} onValueChange={setAutoPayInterval}>
                  <SelectTrigger data-testid="autopay-interval-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAutoPayOpen(false)}>Cancel</Button>
              <Button onClick={handleSetupAutoPay} disabled={autoPayLoading} data-testid="confirm-autopay-btn">
                {autoPayLoading ? "Setting up..." : "Confirm Auto-Pay"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUS_OPTIONS.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {PAYMENT_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {isBoard && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="ml-auto">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Invoice
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Payment Invoice</DialogTitle>
                  <DialogDescription>Create a payment request for an owner</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Owner *</Label>
                    <Select value={newPayment.owner_id} onValueChange={(v) => setNewPayment({...newPayment, owner_id: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select owner" />
                      </SelectTrigger>
                      <SelectContent>
                        {owners.map(o => (
                          <SelectItem key={o.owner_id} value={o.owner_id}>
                            {o.first_name} {o.last_name} ({o.email_primary})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Property (optional)</Label>
                    <Select value={newPayment.property_id} onValueChange={(v) => setNewPayment({...newPayment, property_id: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                      <SelectContent>
                        {properties.map(p => (
                          <SelectItem key={p.property_id} value={p.property_id}>
                            Lot {p.lot_number} - {p.address}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Amount *</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          className="pl-7"
                          value={newPayment.amount}
                          onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Type *</Label>
                      <Select value={newPayment.payment_type} onValueChange={(v) => setNewPayment({...newPayment, payment_type: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {PAYMENT_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={newPayment.description}
                      onChange={(e) => setNewPayment({...newPayment, description: e.target.value})}
                      placeholder="e.g., 2026 Annual HOA Dues"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={newPayment.due_date}
                      onChange={(e) => setNewPayment({...newPayment, due_date: e.target.value})}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                  <Button onClick={handleCreatePayment}>Create Invoice</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Payments List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading payments...</div>
        ) : payments.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <DollarSign className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No payments found</p>
              {!isBoard && (
                <p className="text-sm text-muted-foreground mt-2">You have no outstanding payments</p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {payments.map((payment) => (
              <Card key={payment.payment_id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">${payment.amount.toFixed(2)}</h3>
                        {getStatusBadge(payment.status)}
                        <Badge variant="outline">{getTypeLabel(payment.payment_type)}</Badge>
                      </div>
                      {payment.description && (
                        <p className="text-muted-foreground mb-2">{payment.description}</p>
                      )}
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        {payment.owner_info && (
                          <span>{payment.owner_info.first_name} {payment.owner_info.last_name}</span>
                        )}
                        {payment.due_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Due: {new Date(payment.due_date).toLocaleDateString()}
                          </span>
                        )}
                        {payment.paid_at && (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            Paid: {new Date(payment.paid_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      {payment.status === "pending" && (
                        <Button onClick={() => { setSelectedPayment(payment); setPayOpen(true); }}>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Pay Now
                        </Button>
                      )}
                      {payment.status === "completed" && isBoard && user?.role === "admin" && (
                        <Button variant="outline" onClick={() => handleRefund(payment.payment_id)}>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Refund
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pay Dialog */}
        <Dialog open={payOpen} onOpenChange={setPayOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Complete Payment</DialogTitle>
              <DialogDescription>
                You will be redirected to a secure checkout page
              </DialogDescription>
            </DialogHeader>
            {selectedPayment && (
              <div className="py-6">
                <div className="text-center mb-6">
                  <p className="text-4xl font-bold">${selectedPayment.amount.toFixed(2)}</p>
                  <p className="text-muted-foreground mt-2">{getTypeLabel(selectedPayment.payment_type)}</p>
                  {selectedPayment.description && (
                    <p className="text-sm text-muted-foreground">{selectedPayment.description}</p>
                  )}
                </div>
                
                <div className="p-4 bg-muted rounded-lg text-sm space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>${selectedPayment.amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Processing Fee</span>
                    <span>$0.00</span>
                  </div>
                  <div className="flex justify-between font-semibold pt-2 border-t">
                    <span>Total</span>
                    <span>${selectedPayment.amount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
              <Button onClick={handlePayNow} disabled={processing}>
                {processing ? "Processing..." : "Pay Now"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );

  if (isBoard) {
    return <AdminSidebar>{content}</AdminSidebar>;
  }
  return content;
};

export default PaymentsPage;
